import * as Sentry from '@sentry/react'

import { FC, ReactNode, useEffect, useMemo, useRef } from 'react'
import { Transition, TransitionGroup } from 'react-transition-group'
import { enter, exit } from '../timelines'

import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { useAppMeta } from 'components/higher-order/App/appMeta.context'
import { useAppStyleSwitch } from 'components/higher-order/AppStyleSwitch'
import { useAuth0 } from 'utils/auth'
import { useLocation } from 'react-router-dom'

export const PageTransition: FC<{ children?: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation()
  const {
    isLoading,
    isUserVerified,
    isUserSet,
  } = useAppMeta()

  const { isAuthenticated } = useAuth0()
  const { routeHasNewDesign } = useAppStyleSwitch()
  const allowSimplifiedTransitionKey = useFeatureFlag(FeatureFlag.ALLOW_SIMPLIFIED_TRANSITION_KEY)

  // Add ref to track if component is mounted
  const isMountedRef = useRef(true)

  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const displayKey = useMemo(
    () => allowSimplifiedTransitionKey
      ? pathname
      : [pathname, isLoading, isAuthenticated, isUserVerified, isUserSet].join('-'),
    [allowSimplifiedTransitionKey, isAuthenticated, isLoading, isUserSet, isUserVerified, pathname]
  )

  const canTransition = !routeHasNewDesign

  // NOTE: the white-screen crash on this flow (ISSUE-2153) is a `removeChild`
  // NotFoundError thrown by React's own commit phase when the outgoing view is
  // unmounted on a browser-auto-translated page — NOT by the GSAP timelines
  // below. That is guarded globally in `installSafeDomMutations` (bootstrap).
  // The safeEnter/safeExit guards here only protect the GSAP enter/exit calls.

  // Safe enter function with error handling
  const safeEnter = (node: HTMLDivElement, appears: boolean) => {
    if (!isMountedRef.current || !node) return

    try {
      // Check if node is still in DOM
      if (document.contains(node)) {
        enter(node, appears)
      }
    } catch (error) {
      console.warn('PageTransition enter error:', error)
      Sentry.captureException(error, {
        tags: {
          component: 'PageTransition',
          errorType: 'DOM_MANIPULATION',
          operation: 'enter'
        },
        extra: {
          displayKey,
          canTransition,
          pathname: window.location.pathname
        }
      })
    }
  }

  // Safe exit function with error handling
  const safeExit = (node: HTMLDivElement) => {
    if (!isMountedRef.current || !node) return

    try {
      // Check if node is still in DOM
      if (document.contains(node)) {
        exit(node)
      }
    } catch (error) {
      console.warn('PageTransition exit error:', error)
      Sentry.captureException(error, {
        tags: {
          component: 'PageTransition',
          errorType: 'DOM_MANIPULATION',
          operation: 'exit'
        },
        extra: {
          displayKey,
          canTransition,
          pathname: window.location.pathname
        }
      })
    }
  }

  return (
    <TransitionGroup component={null}>
      <Transition
        key={displayKey}
        appear={true}
        onEnter={
          canTransition
            ? (node, appears) => safeEnter(node as HTMLDivElement, appears)
            : undefined
        }
        onExit={
          canTransition
            ? (node) => safeExit(node as HTMLDivElement)
            : undefined
        }
        timeout={{ enter: 600, exit: 150 }}
      >
        {children}
      </Transition>
    </TransitionGroup>
  )
}
